import React, { Fragment, useState, useEffect, useCallback, memo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { CloudArrowUpIcon, PlusIcon, TrashIcon, XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import { upsertPropertyDefinition, deletePropertyDefinition } from "../../Requests/PropertyRequests";
import { GithubPicker } from 'react-color';
import Loader from "../Loader";

const colors_option_2 = ["#ddad9e", "#f8e0c5", "#fcd39e", "#eab976", "#dec487", "#e6dcab", "#f1efb9", "#e2f1b5", "#bae295", "#aeea84", "#bbe4ae", "#aceea4", "#8cdd85", "#85eca4", "#bdf3d7", "#87fdd1", "#6fdcb4", "#a2dcf1", "#8cd2ef", "#aad2ec", "#bedcf0", "#72b0db", "#9bbbfc", "#a4a3dd", "#a8a1e9", "#c7bdf2", "#b29fdf", "#d789fe", "#e5a3f6", "#f4bff8", "#e895b1", "#f597a4"];

const PropertyCard = memo(({ property, index, handlePropertyChange, setEditingIndex, editingIndex, handleRemoveProperty, t, renderPropertyFields }) => {
    const isEditing = editingIndex === index;

    const getTypeLabel = (type) => {
        switch (type) {
            case 'text':
                return t('SubjectProperties.Text');
            case 'number':
                return t('SubjectProperties.Number');
            case 'date':
                return t('SubjectProperties.Date');
            case 'boolean':
                return t('SubjectProperties.Boolean');
            case 'select':
                return t('SubjectProperties.Select');
            case 'multiple_select':
                return t('SubjectProperties.MultipleSelect');
            default:
                return type;
        }
    };

    return (
        <div
            className="flex flex-col gap-2 p-4 border rounded-lg transition-all duration-200"
            style={{ backgroundColor: property.color || "#ffffff" }}
        >
            {isEditing ? (
                <div className="bg-white bg-opacity-90 rounded-lg p-4">
                    <div className="space-y-4">
                        {renderPropertyFields(property, index)}
                    </div>
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingIndex(null);
                            }}
                            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            {t('SubjectProperties.Close')}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center">
                            <span className="font-medium" style={{ color: property.color }}>
                                {index + 1}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium text-black">
                                {property.name || t('SubjectProperties.Unnamed')}
                            </span>
                            <span className="text-sm text-black text-opacity-80">
                                {getTypeLabel(property.type)}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingIndex(index);
                            }}
                            className="text-black hover:text-gray-700"
                        >
                            <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveProperty(index, property.uuid);
                            }}
                            className="text-black hover:text-gray-700"
                        >
                            <TrashIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

export default function SubjectPropertiesModal({ handleClose, project_uuid, project, onSave }) {

    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [properties, setProperties] = useState(project.property_definitions || []);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        if (project?.property_definitions) {
            setProperties(project.property_definitions);
            setLoading(false);
        }
    }, [project]);

    const closeModal = useCallback(() => {
        handleClose();
        setOpen(false);
    }, [handleClose]);

    const handleAddProperty = useCallback(() => {
        setProperties(prevProperties => [
            ...prevProperties,
            {
                name: "",
                type: "text",
                required: false,
                options: [],
                min_value: null,
                max_value: null,
                color: "#f67088",
                order: prevProperties.length
            }
        ]);
        setEditingIndex(properties.length);
    }, [properties.length]);

    const handleRemoveProperty = useCallback(async (index, uuid) => {
        if (uuid) {
            try {
                await deletePropertyDefinition(project_uuid, uuid);

                // Update the local state immediately after successful deletion
                setProperties(prevProperties => prevProperties.filter((_, i) => i !== index));
                setEditingIndex(null);

                // Update the parent project state if onSave is provided
                if (onSave) {
                    onSave({
                        ...project,
                        property_definitions: properties.filter((_, i) => i !== index)
                    });
                }
            } catch (error) {
                console.error('Error deleting property:', error);
                setError(t('SubjectProperties.DeleteError'));
                return;
            }
        } else {
            // For new properties that don't have a UUID yet
            setProperties(prevProperties => prevProperties.filter((_, i) => i !== index));
            setEditingIndex(null);
        }
    }, [project_uuid, t, onSave, project, properties]);

    const handlePropertyChange = useCallback((index, field, value) => {
        setProperties(prevProperties => {
            const newProperties = [...prevProperties];
            newProperties[index] = {
                ...newProperties[index],
                [field]: value
            };
            return newProperties;
        });
    }, []);

    const handleAddOption = useCallback((propertyIndex) => {
        setProperties(prevProperties => {
            const newProperties = [...prevProperties];
            newProperties[propertyIndex] = {
                ...newProperties[propertyIndex],
                options: [...(newProperties[propertyIndex].options || []), ""]
            };
            return newProperties;
        });
    }, []);

    const handleOptionChange = useCallback((propertyIndex, optionIndex, value) => {
        setProperties(prevProperties => {
            const newProperties = [...prevProperties];
            newProperties[propertyIndex] = {
                ...newProperties[propertyIndex],
                options: newProperties[propertyIndex].options?.map((opt, i) => i === optionIndex ? value : opt) || []
            };
            return newProperties;
        });
    }, []);

    const handleRemoveOption = useCallback((propertyIndex, optionIndex) => {
        setProperties(prevProperties => {
            const newProperties = [...prevProperties];
            newProperties[propertyIndex].options.splice(optionIndex, 1);
            return newProperties;
        });
    }, []);


    const validateProperties = useCallback((propsToValidate) => {
        for (let prop of propsToValidate) {
            if ((prop.type === 'select' || prop.type === 'multiple_select') &&
                (!prop.options || prop.options.length === 0 || prop.options.every(opt => opt === ""))) {
                return t('SubjectProperties.OptionsRequired');
            }
        }
        return null;
    }, [t]);

    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError(null);

        // Validate properties before saving
        const validationError = validateProperties(properties);
        if (validationError) {
            setError(validationError);
            setSaving(false);
            return;
        }

        try {
            const results = await Promise.all(properties.map((property, index) =>
                upsertPropertyDefinition({
                    project_uuid,
                    uuid: property.uuid,
                    name: property.name,
                    type: property.type,
                    options: property.options?.filter(opt => opt !== ""),
                    min_value: property.min_value,
                    max_value: property.max_value,
                    color: property.color,
                    required: property.required,
                    order: index
                })
            ));

            // Update the project's property_definitions with the latest data
            const updatedProject = {
                ...project,
                property_definitions: properties.map((prop, index) => ({
                    ...prop,
                    ...results[index]?.data
                }))
            };

            if (onSave) {
                onSave(updatedProject);
            }

            closeModal();
        } catch (error) {
            console.error('Error saving properties:', error);
            setError(t('SubjectProperties.SaveError'));
        } finally {
            setSaving(false);
        }
    };
    const renderPropertyFields = useCallback((property, index) => (
        <>
            <div>
                <label className="block text-sm font-medium text-gray-700">
                    {t('SubjectProperties.Name')}
                </label>
                <input
                    type="text"
                    value={property.name || ''}
                    onChange={(e) => handlePropertyChange(index, 'name', e.target.value)}
                    className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">
                    {t('SubjectProperties.Type')}
                </label>
                <select
                    value={property.type}
                    onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                    className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                >
                    <option value="text">{t('SubjectProperties.Text')}</option>
                    <option value="number">{t('SubjectProperties.Number')}</option>
                    <option value="date">{t('SubjectProperties.Date')}</option>
                    <option value="boolean">{t('SubjectProperties.Boolean')}</option>
                    <option value="select">{t('SubjectProperties.Select')}</option>
                    <option value="multiple_select">{t('SubjectProperties.MultipleSelect')}</option>
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">
                    {t('SubjectProperties.Color')}
                </label>
                <div className="mt-2 flex justify-center">
                    <GithubPicker
                        width="full"
                        triangle="hide"
                        color={property.color}
                        onChangeComplete={(color) => handlePropertyChange(index, 'color', color.hex)}
                        colors={colors_option_2}
                    />
                </div>
            </div>

            {property.type === 'number' && (
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            {t('SubjectProperties.MinValue')}
                        </label>
                        <input
                            type="number"
                            value={property.min_value || ''}
                            onChange={(e) => handlePropertyChange(index, 'min_value', e.target.value)}
                            className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            {t('SubjectProperties.MaxValue')}
                        </label>
                        <input
                            type="number"
                            value={property.max_value || ''}
                            onChange={(e) => handlePropertyChange(index, 'max_value', e.target.value)}
                            className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                    </div>
                </div>
            )}

            {(property.type === 'select' || property.type === 'multiple_select') && (
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                        <div className="flex">{t('SubjectProperties.Options')} <span className="text-red-600 flex items-start"> *</span></div>
                    </label>
                    {property.options?.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex gap-2">
                            <input
                                type="text"
                                value={option}
                                onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                                placeholder={`${t('SubjectProperties.Option')} ${optionIndex + 1}`}
                            />
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveOption(index, optionIndex);
                                }}
                                className="text-red-500 hover:text-red-700"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddOption(index);
                        }}
                        className="mt-2 flex items-center text-sm text-gray-600 hover:text-gray-900"
                    >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        {t('SubjectProperties.AddOption')}
                    </button>
                </div>
            )}

            <div className="flex items-center mt-2">
                <input
                    type="checkbox"
                    checked={property.required || false}
                    onChange={(e) => handlePropertyChange(index, 'required', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                />
                <label className="ml-2 block text-sm text-gray-900">
                    {t('SubjectProperties.Required')}
                </label>
            </div>
        </>
    ), [handlePropertyChange, handleOptionChange, handleRemoveOption, handleAddOption, t]);
    if (loading) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader className="w-8 h-8" />
            </div>
        );
    }



    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-500 sm:duration-700"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-500 sm:duration-700"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                    <form onSubmit={handleSave} className="flex h-full flex-col bg-white shadow-xl rounded-l-xl">
                                        {/* Header */}
                                        <div className="px-4 py-6 sm:px-6">
                                            <div className="flex items-start justify-between">
                                                <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                                                    {t('SubjectProperties.Title')}
                                                </Dialog.Title>
                                                <div className="ml-3 flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                                        onClick={closeModal}
                                                    >
                                                        <span className="absolute -inset-2.5" />
                                                        <span className="sr-only">Close panel</span>
                                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {error && (
                                            <div className="px-4 sm:px-6">
                                                <div className="rounded-md bg-red-50 p-4 mb-4">
                                                    <div className="flex">
                                                        <div className="text-sm text-red-700">{error}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Scrollable content */}
                                        <div className="flex-1 overflow-y-auto px-4 sm:px-6">
                                            <div className="space-y-4">
                                                {properties.map((property, index) => (
                                                    <PropertyCard
                                                        key={property.uuid || index}
                                                        property={property}
                                                        index={index}
                                                        handlePropertyChange={handlePropertyChange}
                                                        setEditingIndex={setEditingIndex}
                                                        editingIndex={editingIndex}
                                                        handleRemoveProperty={handleRemoveProperty}
                                                        t={t}
                                                        renderPropertyFields={renderPropertyFields}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Footer */}
                                        <div className="flex flex-col gap-4 border-t border-gray-200 px-4 py-6 sm:px-6">
                                            <button
                                                type="button"
                                                onClick={handleAddProperty}
                                                className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                                            >
                                                <PlusIcon className="h-5 w-5 mr-2" />
                                                {t('SubjectProperties.AddProperty')}
                                            </button>

                                            <div className="flex justify-end gap-4">
                                                <button
                                                    type="button"
                                                    onClick={closeModal}
                                                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                    {t('SubjectProperties.Cancel')}
                                                </button>
                                                {saving ? (
                                                    <div className="bg-black text-white rounded-md px-12 py-2 w-fit">
                                                        <Loader />
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="submit"
                                                        className="rounded-md bg-black px-12 py-2 text-sm font-medium text-white hover:bg-gray-800"
                                                    >
                                                        {t('SubjectProperties.Save')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
